import { useState, useMemo, useEffect } from 'react';
import { calculateTotals, toggleProduct, updateProductQuantity, updateProductNotes, removeProduct } from '../../utils/OrderManagement';
import {
    toggleOption, selectionTotal, unmetGroups, flattenSelection,
} from './lib/modifierTree';
import { resolveProductPrice } from '../../utils/productPricing';

const useOrderBuilder = ({ tenantConfig, selectedDiningOption, diningOptionObjects, bulkCalculatedStock, products, setError, t }) => {
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [notes, setNotes] = useState('');
    const [addServiceCharge, setAddServiceCharge] = useState(false);
    const [addTax, setAddTax] = useState(false);
    const [discountAmount, setDiscountAmount] = useState(0);
    const [discountMode, setDiscountMode] = useState('direct');
    const [payableAmount, setPayableAmount] = useState(0);
    const [discountType, setDiscountType] = useState('amount');
    const [discountPercentage, setDiscountPercentage] = useState(0);
    const [showDiscountPanel, setShowDiscountPanel] = useState(false);
    const [showNotesPanel, setShowNotesPanel] = useState(false);

    // Modifier state. `selectedAddons` is retained for legacy callers that
    // still pass a flat list of add-on codes; new selection goes through the
    // tree. See docs/superpowers/specs/2026-08-24-nested-addons-design.md
    const [productAddons, setProductAddons] = useState({});
    const [addonModalOpen, setAddonModalOpen] = useState(false);
    const [currentProduct, setCurrentProduct] = useState(null);
    const [selectedAddons, setSelectedAddons] = useState([]);
    const [modifierTree, setModifierTree] = useState({});
    const [modifierCatalog, setModifierCatalog] = useState({ groups: {}, options: {} });
    const [modifierFlagUnmet, setModifierFlagUnmet] = useState(false);

    // Auto-apply service charge based on dining option's service_charge_applied flag
    useEffect(() => {
        const optionObj = diningOptionObjects?.[selectedDiningOption];
        setAddServiceCharge(optionObj?.service_charge_applied === true);
    }, [selectedDiningOption, diningOptionObjects]);

    // Auto-apply tax
    useEffect(() => {
        const taxApplied = tenantConfig?.tax_applied || [];
        setAddTax(taxApplied.includes(selectedDiningOption));
    }, [selectedDiningOption, tenantConfig]);

    // Memoized totals (with discount)
    const memoizedTotals = useMemo(() => {
        return calculateTotals(
            selectedProducts, addServiceCharge, discountAmount,
            tenantConfig?.service_charge_rate || 0.12,
            addTax, tenantConfig?.tax_rate || 0
        );
    }, [selectedProducts, addServiceCharge, discountAmount, tenantConfig?.service_charge_rate, addTax, tenantConfig?.tax_rate]);

    const handleCalculateTotals = () => memoizedTotals;

    // Update payable when discount or total changes
    useEffect(() => {
        const { subtotal, serviceCharge } = memoizedTotals;
        const totalBeforeDiscount = subtotal + serviceCharge;

        if (discountMode === 'payable') {
            setPayableAmount(totalBeforeDiscount - discountAmount);
        }
    }, [discountMode, memoizedTotals, discountAmount]);

    // Product manipulation
    // A dining option can sell at the product's secondary price (client
    // request 2026-08-26). Resolve it HERE, at the single point a product
    // enters the cart, so everything downstream — totals, the posted
    // transaction, receipts, kitchen tickets — reads one already-correct
    // selling_price rather than each re-deriving it and risking disagreement.
    const priceFor = (product) => resolveProductPrice(
        product, diningOptionObjects?.[selectedDiningOption]
    );

    const atDiningOptionPrice = (product) => (
        product ? { ...product, selling_price: priceFor(product) } : product
    );

    const handleToggleProduct = (product) => {
        toggleProduct(atDiningOptionPrice(product), selectedProducts, setSelectedProducts);
    };

    const handleUpdateProductQuantity = (productId, quantity) => {
        try {
            const hasUniqueIdMatch = selectedProducts.some(p => p.uniqueId && p.uniqueId === productId);
            const productInOrder = selectedProducts.find(p =>
                hasUniqueIdMatch
                    ? (p.uniqueId === productId)
                    : (p.product_code === productId || p.id === productId)
            );
            if (!productInOrder) return;

            const originalProduct = products.find(p => p.product_code === productInOrder.product_code);
            if (!originalProduct) return;

            let availableStock;
            if (originalProduct.stock_calculation_type === 'auto') {
                const stockData = bulkCalculatedStock[originalProduct.product_code];
                availableStock = stockData ? stockData.available_stock : 0;
            } else {
                availableStock = originalProduct.quantity === -9999 ? Infinity : originalProduct.quantity;
            }

            const requested = parseInt(quantity) || 1;
            if (availableStock !== Infinity && requested > availableStock) {
                setError(t('cannot_set_quantity_stock_limit', {
                    requested, available: availableStock, product: productInOrder.name
                }));
                setTimeout(() => setError(''), 3000);
                return;
            }

            updateProductQuantity(productId, quantity, selectedProducts, setSelectedProducts);
        } catch (error) {
            console.error('Error in handleUpdateProductQuantity:', error);
            setError(error.message);
            setTimeout(() => setError(''), 3000);
        }
    };

    const handleRemoveProduct = (productId) => {
        removeProduct(productId, selectedProducts, setSelectedProducts);
    };

    const handleUpdateProductNotes = (productCode, newNotes) => {
        updateProductNotes(productCode, newNotes, selectedProducts, setSelectedProducts);
    };

    // ---- Modifier selection -------------------------------------------------

    // A product's groups come from the catalog. Products that only have legacy
    // product_addons keep working: those are wrapped into one implicit
    // optional pick-any group so nothing about their behaviour changes.
    const LEGACY_GROUP_ID = '__legacy_addons__';

    const resolveProductGroups = (product) => {
        const ids = product?.modifier_group_ids || [];
        if (ids.length > 0) return ids;
        if (product?.available_addons?.length > 0) return [LEGACY_GROUP_ID];
        return [];
    };

    // True when the product claims modifier groups but the catalog resolved
    // NONE of them (e.g. modifierService.getCatalog() failed or hasn't
    // resolved yet). Without this check the modal still opens (groups.length
    // > 0) but renders empty, and unmetGroups() silently returns [] for
    // groups it has no data for — so "Add to order" stayed enabled and a
    // cashier could commit a line with zero modifiers even though the
    // product actually requires some. Catalog-unavailable is treated as an
    // unmet/blocked state instead.
    const catalogGroupsUnresolved = (groupIds, catalog) =>
        groupIds.length > 0 && groupIds.every(id => !catalog?.groups?.[id]);

    const resolveCatalog = (product) => {
        const ids = product?.modifier_group_ids || [];
        if (ids.length > 0 || !product?.available_addons?.length) return modifierCatalog;
        // Build the implicit legacy group on the fly.
        const options = { ...modifierCatalog.options };
        product.available_addons.forEach((a) => {
            options[a.product_code] = {
                product_code: a.product_code, name: a.name,
                price: parseFloat(a.price) || 0, groups: [],
            };
        });
        return {
            groups: {
                ...modifierCatalog.groups,
                [LEGACY_GROUP_ID]: {
                    group_id: LEGACY_GROUP_ID, name: t('add_ons'),
                    min_select: 0, max_select: null, sort_order: 0,
                    options: product.available_addons.map(a => a.product_code),
                },
            },
            options,
        };
    };

    const currentProductGroups = useMemo(
        () => resolveProductGroups(currentProduct),
        [currentProduct] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const activeCatalog = useMemo(
        () => resolveCatalog(currentProduct),
        [currentProduct, modifierCatalog] // eslint-disable-line react-hooks/exhaustive-deps
    );

    const modifierUnmet = useMemo(() => {
        if (catalogGroupsUnresolved(currentProductGroups, activeCatalog)) {
            // NEW translation key — not yet in migrate_ui_translations.py.
            return [t('modifier_catalog_unavailable', {}, "Options unavailable — try again")];
        }
        return unmetGroups(modifierTree, currentProductGroups, activeCatalog);
    }, [modifierTree, currentProductGroups, activeCatalog]); // eslint-disable-line react-hooks/exhaustive-deps

    const modifierLineTotal = useMemo(() => {
        const base = priceFor(currentProduct);
        return base + selectionTotal(modifierTree, currentProductGroups, activeCatalog);
    }, [currentProduct, modifierTree, currentProductGroups, activeCatalog]);

    const toggleModifier = (path, groupId, optionCode) => {
        setModifierTree(prev => toggleOption(prev, path, groupId, optionCode, activeCatalog));
    };

    const handleProductWithAddons = (product) => {
        const groups = resolveProductGroups(product);
        if (groups.length > 0) {
            setCurrentProduct(product);
            setModifierTree({});
            setSelectedAddons([]);
            setModifierFlagUnmet(false);
            setAddonModalOpen(true);
        } else {
            handleToggleProduct(product);
        }
    };

    const addProductWithAddons = () => {
        if (!currentProduct) return;
        if (catalogGroupsUnresolved(currentProductGroups, activeCatalog) ||
            unmetGroups(modifierTree, currentProductGroups, activeCatalog).length > 0) {
            setModifierFlagUnmet(true);
            return;
        }

        const lines = flattenSelection(modifierTree, currentProductGroups, activeCatalog);
        const signature = lines
            .map(l => `${l.parent_code || ''}:${l.product_code}`)
            .sort()
            .join('|');

        const existingIdx = selectedProducts.findIndex((product) => {
            if (product.product_code !== currentProduct.product_code) return false;
            const existing = (product.add_ons || [])
                .map(a => `${a.parent_code || ''}:${a.product_code}`)
                .sort()
                .join('|');
            return existing === signature;
        });

        if (existingIdx >= 0) {
            const updated = [...selectedProducts];
            updated[existingIdx] = {
                ...updated[existingIdx],
                quantity: updated[existingIdx].quantity + 1,
            };
            setSelectedProducts(updated);
        } else {
            setSelectedProducts(prev => [...prev, {
                ...atDiningOptionPrice(currentProduct),
                quantity: 1,
                // Flat array, ordered parent-before-child, with parent pointers.
                // Every existing consumer of add_ons keeps working unchanged.
                add_ons: lines,
                add_on_total: lines.reduce((sum, l) => sum + l.price, 0),
                uniqueId: `${currentProduct.product_code}-${signature}-${Date.now()}`,
            }]);
        }

        setAddonModalOpen(false);
        setCurrentProduct(null);
        setSelectedAddons([]);
        setModifierTree({});
        setModifierFlagUnmet(false);
    };

    // Simple total (no discount — for Waiter)
    const calculateTotal = () => {
        const subtotal = selectedProducts.reduce((total, product) => {
            const productTotal = (parseFloat(product.selling_price) || 0) * (parseInt(product.quantity) || 1);
            const addonTotal = product.add_ons
                ? product.add_ons.reduce((sum, addon) => sum + ((parseFloat(addon.price) || 0) * (parseInt(product.quantity) || 1)), 0)
                : 0;
            return total + productTotal + addonTotal;
        }, 0);

        const serviceCharge = addServiceCharge ? subtotal * (tenantConfig?.service_charge_rate || 0.12) : 0;
        const tax = addTax ? subtotal * (tenantConfig?.tax_rate || 0) : 0;
        const total = subtotal + serviceCharge + tax;
        return { subtotal, serviceCharge, tax, total };
    };

    const resetFormState = (paymentMethods = [], setSelectedPaymentMethod = null) => {
        setSelectedProducts([]);
        setNotes('');
        setDiscountType('amount');
        setDiscountPercentage(0);
        setDiscountAmount(0);
        setPayableAmount(0);
        setAddServiceCharge(false);
        setAddTax(false);
        setDiscountMode('direct');
        setShowDiscountPanel(false);
        setShowNotesPanel(false);
        if (setSelectedPaymentMethod && paymentMethods.length > 0) {
            setSelectedPaymentMethod(paymentMethods[0]);
        }
    };

    return {
        selectedProducts, setSelectedProducts,
        notes, setNotes,
        addServiceCharge, setAddServiceCharge,
        addTax, setAddTax,
        discountAmount, setDiscountAmount,
        discountMode, setDiscountMode,
        discountType, setDiscountType,
        discountPercentage, setDiscountPercentage,
        payableAmount, setPayableAmount,
        showDiscountPanel, setShowDiscountPanel,
        showNotesPanel, setShowNotesPanel,
        productAddons, setProductAddons,
        addonModalOpen, setAddonModalOpen,
        currentProduct, setCurrentProduct,
        selectedAddons, setSelectedAddons,
        modifierTree, setModifierTree,
        modifierCatalog, setModifierCatalog,
        modifierFlagUnmet, setModifierFlagUnmet,
        currentProductGroups,
        activeCatalog,
        modifierUnmet,
        modifierLineTotal,
        toggleModifier,
        memoizedTotals,
        handleCalculateTotals,
        calculateTotal,
        handleToggleProduct,
        handleUpdateProductQuantity,
        handleRemoveProduct,
        handleUpdateProductNotes,
        handleProductWithAddons,
        addProductWithAddons,
        resetFormState,
    };
};

export default useOrderBuilder;
